// ESLint
/* exported elementScrollProgress */
/* eslint complexity: "off" */

/*
scrollProgress.js
Author: Aric Ng <aric@firefall.io>
Version: 1.0.1
Notes:
This was developed as a simple/lightweight scroll progression function that assumes the page has a
natural layout, i.e. everything is in DOM order.

User passes in an array of DOM/HTMLCollection of elements
What comes out is a list of currently visible elements

Current elements get a class triggered if it's set in options

This function can also be hooked up to scroll events _if needed_

ScrollMagic has an interval to track page resize handling
- Note: I like ScrollMagic for scroll based animation, however the initial set up needs to be done
per section
Sector has issues with narrow sections and noted 'inbetween issues'
*/

/**
* @function elementScrollProgress
* @description Tracks a set of elements based on visiblity using the element overlap with the window.
* @param {HTMLCollection} DOMElements Elements to track
* @param {object} option Object whose properties modify the functionality of this script
* @param {string} [option.activeClass=scrollprogress-active] String representing the active class name for each element when it is considered 'active'
* @param {string} [option.visibleClass=scrollprogress-visible] String representing the visible class name for each element when it is considered 'visible'
* @param {boolean} [option.axis=false] If true, the progress script uses the horizontal axis, vertical by default
* @param {string} option.eventGainCurrent Event to fire for an element that has gained active state
* @param {string} option.eventLostCurrent Event to fire for an element that has lost the active state
* @param {string} option.eventGainVisible Event to fire for an element that has gained visible state
* @param {string} option.eventLostVisible Event to fire for an element that has lost visible state
* @param {object} option.offset Object containing values that adjust global offsets per element
* @param {number|function} [option.offset.top = 0] Number or function that returns a number. Positive values "push" the section down, negative, the opposite
* @param {number|function} [option.offset.bottom = 0] Number or function that returns a number. Positive values "push" the section down, negative, the opposite
* @param {number|function} [option.offset.left = 0] Number or function that returns a number. Positive values "push" the section right, negative, the opposite
* @param {number|function} [option.offset.right = 0] Number or function that returns a number. Positive values "push" the section right, negative, the opposite
* @returns {object} Unfiltered element tracking data
*
*/
/* eslint-disable no-unused-vars */
var elementScrollProgress = (function() {
/* eslint-enable no-unused-vars */
  'use strict';

  var prevScrollY = typeof window.pageYOffset !== 'undefined' ? window.pageYOffset : (document.documentElement || document.body.parentNode || document.body).scrollLeft;
  var prevScrollX = typeof window.pageXOffset !== 'undefined' ? window.pageXOffset : (document.documentElement || document.body.parentNode || document.body).scrollTop;
  var DOMPrevVisible = [];

  return function(DOMElements, options) {
    // DOM
    var DOMTarget;
    var DOMElement;
    // LOGIC
    var defaults = {
      activeClass: 'scrollprogress-active',
      visibleClass: 'scrollprogress-visible',
      axis: false,
      eventGainCurrent: undefined,
      eventLostCurrent: undefined,
      eventGainVisible: undefined,
      eventLostVisible: undefined,
      // Global offset
      offset: {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0
      }
    };
    var scrollTop = typeof window.pageYOffset !== 'undefined' ? window.pageYOffset : (document.documentElement || document.body.parentNode || document.body).scrollLeft;
    var scrollBottom = scrollTop + window.innerHeight;
    var scrollLeft = typeof window.pageXOffset !== 'undefined' ? window.pageXOffset : (document.documentElement || document.body.parentNode || document.body).scrollTop;
    var scrollRight = scrollLeft + window.innerWidth;
    var dirY = -(prevScrollY - scrollTop);
    var dirX = -(prevScrollX - scrollLeft);
    var dir;
    var candidates = [];
    var d = 0;

    /**
    * @function extend
    * @description Polyfill for object.assign
    * @param {object} defaults Destination object to extend
    * @param {object} options Object to merge with destination
    * @returns {object} Combined object
    */
    function extend(defaults, options) {
      var result = {};

      if (typeof defaults === 'undefined' || typeof options === 'undefined') {
        return typeof defaults !== 'undefined' ? defaults : typeof options !== 'undefined' ? options : undefined;
      }

      Object.keys(defaults).forEach(function(element) {
        if (typeof defaults[element] === 'object') {
          result[element] = extend(defaults[element], options[element]);
        } else {
          if (typeof options[element] === 'undefined') {
            result[element] = defaults[element];
          } else {
            result[element] = options[element];
          }
        }
      });

      return result;
    }

    /**
    * @function triggerEvent
    * @param {HTMLCollection} target Element to dispatch to
    * @param {string} eventName Event name
    * @param {*} values Any value
    * @returns {undefined}
    */
    function triggerEvent(target, eventName, values) {
      var event;

      // Fallback for browsers where Event and CustomEvent have no constructors
      if (typeof Event !== 'function' || typeof CustomEvent !== 'function') {
        if (typeof values !== 'object') {
          event = document.createEvent('HTMLEvents');
          event.initEvent(eventName, true, true);
        } else {
          event = document.createEvent('CustomEvent');
          event.initCustomEvent(eventName, true, true, values);
        }
      } else {
        if (typeof values !== 'object') {
          event = new Event(eventName);
        } else {
          event = new CustomEvent(eventName, {detail: values});
        }
      }

      target.dispatchEvent(event);
    }

    /**
    * @function getElementOverlap
    * @description Gets the element's overlap based on window
    * @param {HTMLCollection} element Element to get overlap percentage of
    * @param {boolean} axis True for horizontal false for vertical
    * @returns {number} Overlap percentage of element
    */
    function getElementOverlap(element, axis) {
      var domRect = element.getBoundingClientRect();
      var sectionStart;
      var sectionEnd;
      var sectionOffsets = {};
      var scrollStart;
      var scrollEnd;
      var globalOffsets = {};
      var width = domRect.width;
      var height = domRect.height;
      var overlapPercent = 0;

      axis = typeof axis === 'boolean' && axis;

      scrollStart = axis ? scrollLeft : scrollTop;
      scrollEnd = axis ? scrollRight : scrollBottom;

      // Process section offsets
      sectionOffsets = {
        left: parseInt(element.dataset.scrollProgressOffsetLeft),
        right: parseInt(element.dataset.scrollProgressOffsetRight),
        top: parseInt(element.dataset.scrollProgressOffsetTop),
        bottom: parseInt(element.dataset.scrollProgressOffsetBottom)
      };

      Object.keys(sectionOffsets).forEach(function(element) {
        if (typeof sectionOffsets[element] === 'undefined' || sectionOffsets[element] === null || isNaN(sectionOffsets[element])) {
          sectionOffsets[element] = 0;
        }
      });

      // Process global offsets
      Object.keys(options.offset).forEach(function(element) {
        if (typeof options.offset[element] === 'number') {
          globalOffsets[element] = options.offset[element];
        } else if (typeof options.offset[element] === 'function') {
          globalOffsets[element] = options.offset[element]();
        }

        if (typeof globalOffsets[element] === 'undefined' || globalOffsets[element] === null || isNaN(globalOffsets[element])) {
          globalOffsets[element] = 0;
        }
      });

      sectionStart = (axis ? domRect.left : domRect.top) + scrollStart + (axis ? globalOffsets.left : globalOffsets.top) + (axis ? sectionOffsets.left : sectionOffsets.top);
      sectionEnd = (axis ? domRect.width : domRect.height) + sectionStart + (axis ? globalOffsets.right : globalOffsets.bottom) + (axis ? sectionOffsets.right : sectionOffsets.bottom);

      if (scrollEnd >= sectionStart && sectionEnd >= scrollStart) {
        overlapPercent = Math.max (0, Math.min (scrollEnd, sectionEnd) - Math.max (scrollStart, sectionStart) + 1) / (axis ? width : height);
      }

      return overlapPercent;
    }

    options = extend(defaults, options);

    dirY = dirY > 0 ? 1 : dirY === 0 ? 0 : -1;
    dirX = dirX > 0 ? 1 : dirX === 0 ? 0 : -1;

    // Must have elements
    if (typeof DOMElements === 'undefined') {
      return;
    }

    // Set primary dir based on scroll direction
    if (typeof options.axis === 'boolean' && options.axis) {
      dir = dirX;
    } else {
      dir = dirY;
    }

    // Convert HTMLCollection to Array
    if (DOMElements instanceof HTMLCollection) {
      DOMElements = (function() {
        var result = [];
        var e = 0;

        for (e = 0; e < DOMElements.length; e++) {
          result.push(DOMElements[e]);
        }

        return result;
      })();
    }

    // Must be of Array type and have elements
    if (!Array.isArray(DOMElements) || DOMElements.length === 0) {
      return;
    }

    // Scrolling forwards looks at natural DOM order
    // Otherwise reverse DOM order
    if (dir !== 1) {
      DOMElements = DOMElements.reverse();
    }

    // Generate array of candidates consisting of objects containing element + overlap percentage
    for (d = 0; d < DOMElements.length; d++) {
      candidates.push({DOMElement: DOMElements[d], overlap: getElementOverlap(DOMElements[d], options.axis), windowScale: DOMElements[d].getBoundingClientRect().height / window.innerHeight});
    }

    // Elements not visible are ignored
    candidates = candidates.filter(function(element) {
      return element.overlap > 0;
    });

    // Sort candidates based on overlap percentage from high to low
    // Highest value
    candidates = candidates.sort(function(a, b) {
      var result = 0;

      if (a.overlap < b.overlap) {
        result = 1;
      }

      if (a.overlap > b.overlap) {
        result = -1;
      }

      // If both are revealed (has an overlap of 1), throw direction into the mix
      // Scrolling down prioritizes younger siblings
      // Scrolling up prioritizes older siblings
      if (a.overlap > 1 && b.overlap > 1) {
        // Base on direction
        if (dir === 1 || dir === 0) {
          result = -1;
        } else if (dir === -1) {
          // Going up, larger windows take priority
          result = a.windowScale > b.windowScale ? -1 : 1;
        }
      }

      return result;
    });

    // Get the most visible element
    if (candidates.length !== 0) {
      DOMTarget = candidates[0].DOMElement;
    } else { // Do not do any updates if there are no visible elements
      return;
    }

    // If a valid active class is found, mark it active, and remove that class from siblings
    // This assumes active class is even the class to use, defer this elsewhere?
    // gainedActive
    // gainedVisible
    // lostActive
    // lostVisible
    if (typeof DOMTarget !== 'undefined') {
      // BEGIN: Handle current
      if (typeof options.activeClass === 'string') {
        // Trigger event for elements that are going to gain an active class
        DOMTarget.classList.add(options.activeClass);
      }

      if (typeof options.eventGainCurrent === 'string') {
        triggerEvent(DOMTarget, options.eventGainCurrent);
      }

      // Trigger event for elements that had the set active class
      if (typeof options.eventLostCurrent !== 'undefined' || typeof options.activeClass === 'string') {
        for (d = 0; d < DOMElements.length; d++) {
          DOMElement = DOMElements[d];

          if (DOMElement !== DOMTarget) {
            if (typeof options.activeClass === 'string') {
              DOMElement.classList.remove(options.activeClass);
            }

            if (typeof options.eventLostCurrent === 'string') {
              triggerEvent(DOMElement, options.eventLostCurrent);
            }
          }
        }
      }
    }
    // END: Handle current

    // BEGIN: Handle visible elements
    (function() {
      var DOMCandidateElement;
      var DOMVisibleElements = [];

      if (typeof options.elementGainVisible === 'string' || typeof options.visibleClass === 'string') {
        for (d = 0; d < candidates.length; d++) {
          DOMCandidateElement = candidates[d].DOMElement;
          if (DOMPrevVisible.indexOf(DOMCandidateElement) === -1) {
            if (typeof options.eventGainVisible === 'string') {
              triggerEvent(DOMCandidateElement, options.eventGainVisible);
            }

            if (typeof options.visibleClass === 'string') {
              DOMCandidateElement.classList.add(options.visibleClass);
            }
          }
          DOMVisibleElements.push(DOMCandidateElement);
        }
      }

      if (typeof options.eventLostVisible === 'string' || typeof options.visibleClass === 'string') {
        for (d = 0; d < DOMElements.length; d++) {
          DOMElement = DOMElements[d];
          //console.log(DOMVisibleElements, prevVisible, DOMElement);
          // If element is not a visible element and it was previously visible or there is no data for previously visible elements
          if (DOMVisibleElements.indexOf(DOMElement) === -1 && (DOMPrevVisible.indexOf(DOMElement) !== -1 || DOMPrevVisible.length === 0)) {
            if (typeof options.eventLostVisible === 'string') {
              triggerEvent(DOMElement, options.eventLostVisible);
            }

            if (typeof options.visibleClass === 'string') {
              DOMElement.classList.remove(options.visibleClass);
            }
          }
        }
      }

      DOMPrevVisible = DOMVisibleElements;
    })();
    // END: Handle visible elements

    prevScrollY = scrollTop;
    prevScrollX = scrollLeft;

    return candidates;
  };
})();
